/**
 * GuidedPlaceDialog — interactive canvas that lets the designer drag /
 * scale / rotate the jewellery onto each scene image BEFORE Compose.
 *
 * Once positioned, the dialog produces N pre-composite Blobs (jewellery
 * roughly pasted onto each scene at the chosen position/scale/rotation).
 * Those Blobs replace the original scene uploads in the multipart
 * payload, and the backend runs Gemini with a much shorter
 * "photoreal-finalise this layout" prompt — same single-scene path,
 * same async batch pipeline, same multi-format export.
 *
 * UI affordance:
 *   The jewellery is drawn at reduced opacity (PREVIEW_ALPHA) over the
 *   scene so the underlying model/hand is visible while positioning.
 *   A blue bounding box with corner-scale handles + a top rotate handle
 *   sits over the jewellery — replaces the old bottom toolbar buttons.
 *   The committed Blob always renders the jewellery at full opacity.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronLeft, ChevronRight, Loader2, Move, Sparkles,
  FlipHorizontal, RotateCcw, BookmarkPlus, BookOpen, Trash2, X,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

// Output dimension of every pre-composite. 1536px keeps catalogue detail
// without exploding the upload payload. The backend resizes downstream.
const OUTPUT_PX = 1536;

// Jewellery opacity in the PREVIEW canvas only — lets the designer see
// the model/hand underneath while placing. Commit always uses 1.0.
const PREVIEW_ALPHA = 0.65;

// Bounding-box visuals (canvas pixels, scaled down to CSS by the browser).
const BOX_STROKE = "#2563EB";
const HANDLE_FILL = "#2563EB";
const HANDLE_BORDER = "#FFFFFF";
const HANDLE_SIZE = 28;             // square handle side, canvas px
const ROTATE_HANDLE_OFFSET = 70;    // distance above top edge to rotate dot
const ROTATE_HANDLE_RADIUS = 18;
const HIT_PAD = 8;                  // extra pixels around a handle for hit-testing

const fileToImage = (fileOrBlob) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(fileOrBlob);
    const img = new Image();
    img.onload = () => { resolve(img); /* keep URL alive — caller revokes */ };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });

/**
 * One scene's placement state: where on the scene the jewellery sits,
 * how big, and its rotation. All values are normalised to the canvas's
 * output px so the same state renders identically at any preview size.
 */
const defaultPlacement = (sceneAR, jewelAR) => {
  // Start the jewellery at ~30% of the canvas width, centred, no rotation.
  const w = OUTPUT_PX * 0.30;
  const h = w / jewelAR;
  return {
    x: (OUTPUT_PX - w) / 2,
    y: (OUTPUT_PX / sceneAR - h) / 2,
    w,
    h,
    rotation: 0,        // radians
    flipX: false,
  };
};

// Rotate (lx, ly) around origin by `rot` radians.
const rotate2d = (lx, ly, rot) => {
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  return { x: lx * c - ly * s, y: lx * s + ly * c };
};

// World-space position of one of the 4 corner handles (cornerSign in {-1,1}).
const cornerPoint = (p, sx, sy) => {
  const cx = p.x + p.w / 2;
  const cy = p.y + p.h / 2;
  const local = rotate2d((sx * p.w) / 2, (sy * p.h) / 2, p.rotation);
  return { x: cx + local.x, y: cy + local.y };
};

// World-space position of the rotate handle (above the top edge).
const rotateHandlePoint = (p) => {
  const cx = p.x + p.w / 2;
  const cy = p.y + p.h / 2;
  const local = rotate2d(0, -(p.h / 2 + ROTATE_HANDLE_OFFSET), p.rotation);
  return { x: cx + local.x, y: cy + local.y };
};

// Is world-space pointer inside the (possibly rotated) bounding box?
const pointInsideBox = (p, wx, wy) => {
  const cx = p.x + p.w / 2;
  const cy = p.y + p.h / 2;
  const local = rotate2d(wx - cx, wy - cy, -p.rotation);
  return Math.abs(local.x) <= p.w / 2 && Math.abs(local.y) <= p.h / 2;
};

export default function GuidedPlaceDialog({
  open,
  onOpenChange,
  jewelleryFile,          // File | Blob — the original clean jewellery image
  sceneFiles,             // File[] — one or more scene uploads
  onConfirm,              // async (compositeBlobs: Blob[]) => void
  savedLayouts = [],      // Lookbook layouts — [{ id, name, placements: [...] }]
  onSaveLayout,           // async (name: string, placements: norm[]) => row | null
  onDeleteLayout,         // async (layout) => void
}) {
  const [jewelleryImg, setJewelleryImg] = useState(null);
  const [sceneImgs, setSceneImgs] = useState([]);          // HTMLImageElement[]
  const [placements, setPlacements] = useState([]);        // per-scene
  const [activeIdx, setActiveIdx] = useState(0);
  const [committing, setCommitting] = useState(false);
  const [cursor, setCursor] = useState("grab");
  // Lookbook layout UI state.
  const [showLayoutsMenu, setShowLayoutsMenu] = useState(false);
  const [showSaveLayout, setShowSaveLayout] = useState(false);
  const [layoutName, setLayoutName] = useState("");
  const [savingLayout, setSavingLayout] = useState(false);
  const canvasRef = useRef(null);

  // Active gesture state.
  // { kind: 'move'|'scale'|'rotate', ...startContext }
  const dragRef = useRef(null);

  // Load all images when dialog opens.
  useEffect(() => {
    let alive = true;
    if (!open) {
      // Reset transient UI state every time the dialog closes so a
      // re-open never leaves the layouts dropdown / save form open.
      setShowLayoutsMenu(false);
      setShowSaveLayout(false);
      setLayoutName("");
      return;
    }
    (async () => {
      try {
        const jew = await fileToImage(jewelleryFile);
        if (!alive) return;
        setJewelleryImg(jew);
        const scenes = await Promise.all(sceneFiles.map(fileToImage));
        if (!alive) return;
        setSceneImgs(scenes);
        // Seed placements per scene. Each starts the jewellery at 30% width
        // of the canvas, centred over the scene.
        const jewelAR = jew.naturalWidth / jew.naturalHeight;
        const seeded = scenes.map((s) =>
          defaultPlacement(s.naturalWidth / s.naturalHeight, jewelAR),
        );
        setPlacements(seeded);
        setActiveIdx(0);
      } catch {
        // Closing the dialog without all images loaded is fine — user
        // will see an empty canvas and can retry.
      }
    })();
    return () => { alive = false; };
  }, [open, jewelleryFile, sceneFiles]);

  // The on-screen canvas size depends on the active scene's aspect ratio.
  const activeScene = sceneImgs[activeIdx];
  const activePlacement = placements[activeIdx];
  const sceneAR = activeScene ? activeScene.naturalWidth / activeScene.naturalHeight : 1;
  const canvasWidth = OUTPUT_PX;
  const canvasHeight = OUTPUT_PX / sceneAR;

  // Draw routine. Re-runs whenever placement or active index changes.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !activeScene || !activePlacement || !jewelleryImg) return;
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    ctx.drawImage(activeScene, 0, 0, canvasWidth, canvasHeight);

    // Jewellery layer — preview only, drawn at reduced opacity so the
    // designer can see the model/hand below.
    const { x, y, w, h, rotation, flipX } = activePlacement;
    ctx.save();
    ctx.globalAlpha = PREVIEW_ALPHA;
    ctx.translate(x + w / 2, y + h / 2);
    ctx.rotate(rotation);
    if (flipX) ctx.scale(-1, 1);
    ctx.drawImage(jewelleryImg, -w / 2, -h / 2, w, h);
    ctx.restore();

    // Bounding box + handles — drawn at full opacity, on top of jewellery.
    ctx.save();
    ctx.translate(x + w / 2, y + h / 2);
    ctx.rotate(rotation);
    ctx.lineWidth = Math.max(3, canvasWidth / 360);
    ctx.strokeStyle = BOX_STROKE;
    ctx.strokeRect(-w / 2, -h / 2, w, h);

    // Stalk from top-edge centre up to the rotate handle.
    ctx.beginPath();
    ctx.moveTo(0, -h / 2);
    ctx.lineTo(0, -h / 2 - ROTATE_HANDLE_OFFSET);
    ctx.stroke();

    // 4 corner handles (square).
    const drawSquareHandle = (lx, ly) => {
      ctx.fillStyle = HANDLE_FILL;
      ctx.strokeStyle = HANDLE_BORDER;
      ctx.lineWidth = Math.max(2, canvasWidth / 540);
      ctx.fillRect(lx - HANDLE_SIZE / 2, ly - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
      ctx.strokeRect(lx - HANDLE_SIZE / 2, ly - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
    };
    drawSquareHandle(-w / 2, -h / 2);
    drawSquareHandle( w / 2, -h / 2);
    drawSquareHandle( w / 2,  h / 2);
    drawSquareHandle(-w / 2,  h / 2);

    // Rotate handle (circle) above the top edge.
    ctx.beginPath();
    ctx.fillStyle = HANDLE_FILL;
    ctx.strokeStyle = HANDLE_BORDER;
    ctx.lineWidth = Math.max(2, canvasWidth / 540);
    ctx.arc(0, -h / 2 - ROTATE_HANDLE_OFFSET, ROTATE_HANDLE_RADIUS, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    ctx.restore();
  }, [activeScene, activePlacement, jewelleryImg, canvasWidth, canvasHeight]);

  const updateActive = (mut) =>
    setPlacements((arr) => arr.map((p, i) => (i === activeIdx ? { ...p, ...mut(p) } : p)));

  // Convert clientX/Y → canvas-px (taking the CSS scale into account).
  const toCanvasCoords = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return {
      x: ((e.clientX - r.left) / r.width) * canvasWidth,
      y: ((e.clientY - r.top) / r.height) * canvasHeight,
    };
  };

  // Pick which handle (if any) the pointer is over.
  const hitTestHandle = (p, wx, wy) => {
    const half = HANDLE_SIZE / 2 + HIT_PAD;
    const corners = [
      { sx: -1, sy: -1 },
      { sx:  1, sy: -1 },
      { sx:  1, sy:  1 },
      { sx: -1, sy:  1 },
    ];
    for (const c of corners) {
      const w = cornerPoint(p, c.sx, c.sy);
      if (Math.abs(wx - w.x) <= half && Math.abs(wy - w.y) <= half) {
        return { kind: "scale", cornerSign: c };
      }
    }
    const rh = rotateHandlePoint(p);
    if (Math.hypot(wx - rh.x, wy - rh.y) <= ROTATE_HANDLE_RADIUS + HIT_PAD) {
      return { kind: "rotate" };
    }
    return null;
  };

  // Pointer-down detects which control was grabbed.
  const onPointerDown = (e) => {
    if (!activePlacement) return;
    canvasRef.current.setPointerCapture(e.pointerId);
    const p = toCanvasCoords(e);
    const cx = activePlacement.x + activePlacement.w / 2;
    const cy = activePlacement.y + activePlacement.h / 2;

    const hit = hitTestHandle(activePlacement, p.x, p.y);
    if (hit?.kind === "scale") {
      dragRef.current = {
        kind: "scale",
        start: activePlacement,
        cx, cy,
      };
      setCursor("nwse-resize");
      return;
    }
    if (hit?.kind === "rotate") {
      const startAngle = Math.atan2(p.y - cy, p.x - cx);
      dragRef.current = {
        kind: "rotate",
        cx, cy,
        startAngle,
        startRotation: activePlacement.rotation,
      };
      setCursor("grabbing");
      return;
    }
    if (pointInsideBox(activePlacement, p.x, p.y)) {
      dragRef.current = {
        kind: "move",
        offsetX: p.x - activePlacement.x,
        offsetY: p.y - activePlacement.y,
      };
      setCursor("grabbing");
    }
  };

  const onPointerMove = (e) => {
    if (!activePlacement) return;
    const p = toCanvasCoords(e);

    if (!dragRef.current) {
      // Hover-only: update cursor so handles feel discoverable.
      const hit = hitTestHandle(activePlacement, p.x, p.y);
      if (hit?.kind === "scale") setCursor("nwse-resize");
      else if (hit?.kind === "rotate") setCursor("grab");
      else if (pointInsideBox(activePlacement, p.x, p.y)) setCursor("move");
      else setCursor("default");
      return;
    }

    if (dragRef.current.kind === "move") {
      const { offsetX, offsetY } = dragRef.current;
      updateActive(() => ({ x: p.x - offsetX, y: p.y - offsetY }));
    } else if (dragRef.current.kind === "scale") {
      const { start, cx, cy } = dragRef.current;
      // Distance from centre to the dragged corner controls overall size.
      // Original corner distance is half the box diagonal — use that ratio
      // to scale w/h uniformly (preserves aspect).
      const startDiag = Math.hypot(start.w, start.h) / 2;
      const newDiag = Math.hypot(p.x - cx, p.y - cy);
      const factor = Math.max(0.05, newDiag / Math.max(1, startDiag));
      const newW = Math.max(40, Math.min(canvasWidth * 2, start.w * factor));
      const newH = newW * (start.h / start.w);
      updateActive(() => ({
        x: cx - newW / 2,
        y: cy - newH / 2,
        w: newW,
        h: newH,
      }));
    } else if (dragRef.current.kind === "rotate") {
      const { cx, cy, startAngle, startRotation } = dragRef.current;
      const angle = Math.atan2(p.y - cy, p.x - cx);
      updateActive(() => ({ rotation: startRotation + (angle - startAngle) }));
    }
  };

  const onPointerUp = (e) => {
    dragRef.current = null;
    setCursor("grab");
    try { canvasRef.current.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  };

  const resetActive = () => {
    if (!jewelleryImg || !activeScene) return;
    const jewelAR = jewelleryImg.naturalWidth / jewelleryImg.naturalHeight;
    const sAR = activeScene.naturalWidth / activeScene.naturalHeight;
    updateActive(() => defaultPlacement(sAR, jewelAR));
  };
  const flipActive = () => updateActive((p) => ({ flipX: !p.flipX }));

  // ---------- Lookbook layouts: normalise <-> reconstruct ----------
  //
  // A "lookbook layout" stores ONLY the layout, normalised so it can be
  // re-applied to ANY scene AR and ANY jewellery AR. Conversion rules:
  //
  //   to-norm: per-scene canvas height = OUTPUT_PX / sceneAR
  //     cx_norm = (x + w/2) / OUTPUT_PX
  //     cy_norm = (y + h/2) / canvas_height
  //     w_norm  =  w / OUTPUT_PX
  //
  //   from-norm: at apply-time the JEWELLERY may have a different AR than
  //   when the layout was saved, so we derive h from w / new jewelAR.
  //     w = OUTPUT_PX * w_norm
  //     h = w / jewelAR
  //     x = cx_norm * OUTPUT_PX - w/2
  //     y = cy_norm * canvas_height - h/2
  const placementToNorm = (p, sceneAR) => {
    const canvasH = OUTPUT_PX / sceneAR;
    return {
      cx_norm: (p.x + p.w / 2) / OUTPUT_PX,
      cy_norm: (p.y + p.h / 2) / canvasH,
      w_norm: p.w / OUTPUT_PX,
      rotation: p.rotation,
      flip_x: !!p.flipX,
    };
  };
  const normToPlacement = (n, sceneAR, jewelAR) => {
    const canvasH = OUTPUT_PX / sceneAR;
    const w = OUTPUT_PX * n.w_norm;
    const h = w / jewelAR;
    return {
      x: n.cx_norm * OUTPUT_PX - w / 2,
      y: n.cy_norm * canvasH - h / 2,
      w,
      h,
      rotation: n.rotation || 0,
      flipX: !!n.flip_x,
    };
  };

  // Save current placements (across all scenes in this run) as a layout.
  const saveCurrentLayout = async () => {
    if (savingLayout) return;
    const name = layoutName.trim();
    if (!name || !onSaveLayout || sceneImgs.length === 0) return;
    setSavingLayout(true);
    try {
      const norm = placements.map((p, i) => {
        const s = sceneImgs[i];
        return placementToNorm(p, s.naturalWidth / s.naturalHeight);
      });
      const saved = await onSaveLayout(name, norm);
      if (saved) {
        setLayoutName("");
        setShowSaveLayout(false);
        setShowLayoutsMenu(false);
      }
    } finally {
      setSavingLayout(false);
    }
  };

  // Apply a saved layout to the current jewellery × scenes. If the
  // saved layout has fewer slots than the current scene count, the
  // remaining scenes keep their existing placement (often the seeded
  // default, which is fine — designer can tweak from there). If it has
  // more, extras are simply ignored.
  const applyLayout = (layout) => {
    if (!jewelleryImg || sceneImgs.length === 0) return;
    const jewelAR = jewelleryImg.naturalWidth / jewelleryImg.naturalHeight;
    setPlacements((prev) =>
      prev.map((cur, i) => {
        const norm = layout.placements?.[i];
        if (!norm) return cur;
        const s = sceneImgs[i];
        return normToPlacement(norm, s.naturalWidth / s.naturalHeight, jewelAR);
      }),
    );
    setShowLayoutsMenu(false);
  };

  // Commit: render every scene's canvas at OUTPUT_PX resolution and
  // emit Blobs to the parent. Jewellery is drawn at FULL OPACITY here
  // (preview-alpha is purely a placement affordance).
  const commit = async () => {
    if (committing || sceneImgs.length === 0 || !jewelleryImg) return;
    setCommitting(true);
    try {
      const blobs = [];
      for (let i = 0; i < sceneImgs.length; i += 1) {
        const s = sceneImgs[i];
        const p = placements[i];
        const c = document.createElement("canvas");
        const sAR = s.naturalWidth / s.naturalHeight;
        c.width = OUTPUT_PX;
        c.height = Math.round(OUTPUT_PX / sAR);
        const ctx = c.getContext("2d");
        ctx.drawImage(s, 0, 0, c.width, c.height);
        ctx.save();
        ctx.translate(p.x + p.w / 2, p.y + p.h / 2);
        ctx.rotate(p.rotation);
        if (p.flipX) ctx.scale(-1, 1);
        ctx.drawImage(jewelleryImg, -p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
        const blob = await new Promise((res) => c.toBlob(res, "image/jpeg", 0.92));
        blobs.push(blob);
      }
      await onConfirm?.(blobs);
      onOpenChange?.(false);
    } finally {
      setCommitting(false);
    }
  };

  const stepNum = activeIdx + 1;
  const stepTotal = sceneImgs.length;

  const ready = useMemo(
    () => sceneImgs.length > 0 && !!jewelleryImg && placements.length === sceneImgs.length,
    [sceneImgs.length, jewelleryImg, placements.length],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="guided-place-dialog"
        className="max-w-4xl bg-[#0B0B0B] border-white/10 text-white max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Move className="w-4 h-4 text-[#D4AF37]" /> Guided place — position the jewellery
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            Drag inside the box to move · pull a corner to resize · use the top dot to rotate. The jewellery is shown semi-transparent so you can see the model underneath — the final render is fully opaque.
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        {stepTotal > 1 && (
          <div className="flex items-center justify-between mt-2">
            <button
              type="button"
              onClick={() => setActiveIdx((i) => Math.max(0, i - 1))}
              disabled={activeIdx === 0}
              data-testid="guided-prev-scene"
              className="inline-flex items-center gap-1 text-[12px] text-white/65 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/55">
              Scene {stepNum} / {stepTotal}
            </div>
            <button
              type="button"
              onClick={() => setActiveIdx((i) => Math.min(stepTotal - 1, i + 1))}
              disabled={activeIdx === stepTotal - 1}
              data-testid="guided-next-scene"
              className="inline-flex items-center gap-1 text-[12px] text-white/65 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Canvas */}
        <div className="mt-2 rounded-xl border border-white/10 bg-[#141414] p-2 overflow-hidden">
          {ready ? (
            <canvas
              ref={canvasRef}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              data-testid="guided-place-canvas"
              style={{
                width: "100%",
                height: "auto",
                touchAction: "none",
                cursor,
              }}
            />
          ) : (
            <div className="h-72 flex items-center justify-center text-white/40 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading scene…
            </div>
          )}
        </div>

        {/* Minimal toolbar — only the actions the bounding box can't do. */}
        <div className="mt-3 flex items-center justify-center gap-1.5 flex-wrap">
          <button type="button" onClick={flipActive} data-testid="guided-flip"
                  className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-md border border-white/10 text-white/75 hover:text-[#D4AF37] hover:border-[#D4AF37]/45 transition">
            <FlipHorizontal className="w-3 h-3" /> Flip horizontally
          </button>
          <button type="button" onClick={resetActive} data-testid="guided-reset"
                  className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-md border border-white/10 text-white/55 hover:text-red-400 hover:border-red-400/45 transition">
            <RotateCcw className="w-3 h-3" /> Reset placement
          </button>

          {/* Lookbook layouts — apply / save a layout (position, size,
              rotation only — no jewellery, no scene). Designer-only
              feature, gracefully no-ops if the parent didn't wire the
              save / delete callbacks. */}
          {onSaveLayout && (
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setShowLayoutsMenu((v) => !v);
                  setShowSaveLayout(false);
                }}
                data-testid="guided-layouts-toggle"
                className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-md border border-[#D4AF37]/35 text-[#D4AF37]/90 hover:border-[#D4AF37]/65 hover:text-[#D4AF37] transition"
              >
                <BookOpen className="w-3 h-3" /> Lookbook layouts
                <span className="text-[9px] text-[#D4AF37]/55 tabular-nums">· {savedLayouts.length}</span>
              </button>
              {showLayoutsMenu && (
                <div
                  data-testid="guided-layouts-menu"
                  className="absolute z-30 mt-1.5 left-1/2 -translate-x-1/2 w-72 rounded-lg border border-[#D4AF37]/40 bg-[#0E0E0E] shadow-2xl p-2 text-left"
                >
                  <div className="flex items-center justify-between px-1.5 pb-1.5 border-b border-white/5">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">
                      Apply a layout
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowLayoutsMenu(false)}
                      data-testid="guided-layouts-close"
                      className="text-white/40 hover:text-white"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  {savedLayouts.length === 0 && !showSaveLayout && (
                    <div className="text-[11px] text-white/40 px-2 py-2">
                      No saved layouts yet — position the jewellery, then save the setup below.
                    </div>
                  )}
                  <div className="max-h-44 overflow-y-auto">
                    {savedLayouts.map((l) => (
                      <div
                        key={l.id}
                        className="group flex items-center justify-between gap-1 px-1.5 py-1 rounded hover:bg-white/5"
                      >
                        <button
                          type="button"
                          onClick={() => applyLayout(l)}
                          data-testid={`guided-layout-apply-${l.id}`}
                          className="flex-1 text-left text-[12px] text-white/85 hover:text-[#D4AF37] truncate"
                          title={l.name}
                        >
                          {l.name}
                          <span className="text-[10px] text-white/40 ml-1">
                            · {l.placements?.length || 0} {l.placements?.length === 1 ? "scene" : "scenes"}
                          </span>
                        </button>
                        {onDeleteLayout && (
                          <button
                            type="button"
                            onClick={() => onDeleteLayout(l)}
                            data-testid={`guided-layout-delete-${l.id}`}
                            className="opacity-0 group-hover:opacity-100 text-white/35 hover:text-red-400 transition"
                            title="Delete layout"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="pt-2 mt-1 border-t border-white/5">
                    {!showSaveLayout ? (
                      <button
                        type="button"
                        onClick={() => setShowSaveLayout(true)}
                        data-testid="guided-layout-save-toggle"
                        className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] px-2 py-1.5 rounded-md border border-[#D4AF37]/45 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition"
                      >
                        <BookmarkPlus className="w-3 h-3" /> Save current layout
                      </button>
                    ) : (
                      <div className="space-y-1.5" data-testid="guided-layout-save-form">
                        <input
                          type="text"
                          value={layoutName}
                          onChange={(e) => setLayoutName(e.target.value)}
                          placeholder="Layout name (e.g. Bridal ring · right hand)"
                          maxLength={60}
                          data-testid="guided-layout-name-input"
                          className="w-full text-[12px] bg-[#1A1A1A] border border-white/10 rounded px-2 py-1.5 text-white placeholder:text-white/30 focus:border-[#D4AF37]/55 focus:outline-none"
                        />
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={saveCurrentLayout}
                            disabled={!layoutName.trim() || savingLayout}
                            data-testid="guided-layout-save-confirm"
                            className="flex-1 text-[11px] px-2 py-1.5 rounded-md bg-[#D4AF37] text-black font-medium hover:bg-[#D4AF37]/90 transition disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {savingLayout ? "Saving…" : "Save"}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setShowSaveLayout(false); setLayoutName(""); }}
                            className="text-[11px] px-2 py-1.5 rounded-md border border-white/10 text-white/55 hover:text-white"
                          >
                            Cancel
                          </button>
                        </div>
                        <div className="text-[10px] text-white/40">
                          Saves position, size & rotation across {sceneImgs.length} {sceneImgs.length === 1 ? "scene" : "scenes"}.
                          Reusable with any jewellery.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-[11px] text-white/45">
            {stepTotal === 1 ? "1 scene" : `${stepTotal} scenes`} · placement locks position, size, rotation
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onOpenChange?.(false)}
              className="text-[12px] px-3 py-1.5 rounded-md border border-white/10 text-white/65 hover:text-white hover:border-white/25 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={commit}
              disabled={!ready || committing}
              data-testid="guided-confirm-btn"
              className="text-[12px] inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md border border-[#D4AF37]/55 bg-[#D4AF37] text-black font-medium hover:bg-[#D4AF37]/90 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {committing ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparing…</>
              ) : (
                <><Sparkles className="w-3.5 h-3.5" /> Photoreal-finalise {stepTotal} {stepTotal === 1 ? "scene" : "scenes"}</>
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
